// =====================================================================
// iSentinel360 v0.4.1 — OfflineShield (aligned with alert shield)
//
// === Position: matches the GuardDashboard alert shield exactly ===
//
// The GuardDashboard's "alerts in progress" shield (line 1456)
// uses this container:
//
//   <div className="flex-1 flex flex-col items-center justify-center">
//     <div className="relative w-48 h-48">
//       <Shield ... />
//       <span>{count}</span>
//     </div>
//     <h2 className="text-2xl font-semibold mt-8 mb-2">{title}</h2>
//   </div>
//
// The `flex-1` is the key — it makes the container take ALL
// available vertical space in the parent flex column, which
// centers the content naturally. The previous version used
// `min-h-[400px]` which forced a minimum height that didn't
// match the alert shield's behavior — the offline shield ended
// up "off" vertically.
//
// This version uses `flex-1 flex flex-col items-center
// justify-center` to match the alert shield's container
// exactly. The shield, the WiFiOff overlay, and the title
// are all the same size/styling as the alert shield, just
// with red coloring and the WiFiOff icon instead of the count.
// =====================================================================

import { Shield, WifiOff } from 'lucide-react';
import { getNoConnectionText } from '../lib/offlineStrings';

export default function OfflineShield() {
  const language =
    typeof navigator !== 'undefined' ? navigator.language : 'en';
  const title = getNoConnectionText(language);

  return (
    // SAME container as the alert shield (line 1451):
    //   flex-1 + flex + flex-col + items-center + justify-center
    // flex-1 makes this take all available vertical space, so
    // the content is naturally centered at the same position as
    // the alert shield in GuardDashboard.
    <div
      className="flex-1 flex flex-col items-center justify-center"
      data-testid="offline-shield"
    >
      {/* Same 192px square, same animation language as the alert
          shield. The user wanted the offline state to feel like
          an alert — same visual gravity, but with WiFi crossed
          out instead of a count. */}
      <div className="relative w-48 h-48">
        <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping" />
        <div className="absolute inset-0 bg-red-500/10 rounded-full animate-pulse" />
        <Shield className="w-48 h-48 text-red-600 dark:text-red-400" strokeWidth={1.5} />
        <WifiOff
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 text-red-800 dark:text-red-200"
          strokeWidth={2.5}
        />
      </div>
      {/* SAME title styling as the alert shield (line 1461):
            text-2xl font-semibold mt-8 mb-2
          but with red coloring and centered text. */}
      <h1 className="text-2xl font-semibold mt-8 mb-2 text-red-600 dark:text-red-400 text-center">
        {title}
      </h1>
    </div>
  );
}
